import { SortOrder } from '@price-for-profit/micro-services';
import { createContext, PropsWithChildren, useCallback, useContext, useReducer } from 'react';
import { useLocalStorage } from 'shared/hooks';
import { Brands, SearchableFields, SortableFields } from 'shared/types';

interface State {
  show: boolean;
  page: number;
  sortOrder: SortOrder;
  sortBy: SortableFields;
  search: string;
  searchColumn: SearchableFields;
  salesRepOnly: boolean;
}

type Action =
  | { type: 'toggle-show' }
  | { type: 'set-page'; payload: number }
  | { type: 'set-sort-by'; payload: SortableFields }
  | { type: 'set-sort-order'; payload: SortOrder }
  | { type: 'set-search'; payload: string }
  | { type: 'set-search-column'; payload: SearchableFields }
  | { type: 'toggle-sales-rep-only' };

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'toggle-show':
      return { ...state, show: !state.show };
    case 'set-page':
      return { ...state, page: action.payload };
    case 'set-sort-by':
      return { ...state, sortBy: action.payload };
    case 'set-sort-order':
      return { ...state, sortOrder: action.payload };
    case 'set-search':
      return { ...state, search: action.payload };
    case 'set-search-column':
      return { ...state, searchColumn: action.payload };
    case 'toggle-sales-rep-only':
      return { ...state, salesRepOnly: !state.salesRepOnly };
    default:
      return state;
  }
}
const defaultState: State = {
  show: false,
  page: 1,
  sortOrder: 'desc',
  sortBy: 'createdDate',
  search: '',
  searchColumn: 'quoteNumber',
  salesRepOnly: true,
};

interface QuoteListContext {
  state: State;
  toggleShow: () => void;
  setPage: (page: number) => void;
  setSortBy: (sort: SortableFields) => void;
  setSortOrder: (sort: SortOrder) => void;
  setSearch: (search: string) => void;
  setSearchColumn: (column: SearchableFields) => void;
  toggleSalesRepOnly: () => void;
  saveState: (state: State) => void;
}
const quoteListContext = createContext({} as QuoteListContext);

export function ProvideQuoteListState({ children, brand }: PropsWithChildren<{ brand: Brands }>) {
  const [initialState, saveState] = useLocalStorage<State>(`${brand}-quote`, defaultState);
  const [state, dispatch] = useReducer(reducer, initialState);

  const toggleShow = useCallback(() => {
    dispatch({ type: 'toggle-show' });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'set-page', payload: page });
  }, []);

  const setSortBy = useCallback((sort: SortableFields) => {
    dispatch({ type: 'set-sort-by', payload: sort });
  }, []);

  const setSortOrder = useCallback((order: SortOrder) => {
    dispatch({ type: 'set-sort-order', payload: order });
  }, []);

  const setSearch = useCallback((search: string) => {
    dispatch({ type: 'set-search', payload: search });
  }, []);
  const setSearchColumn = useCallback((column: SearchableFields) => {
    dispatch({ type: 'set-search-column', payload: column });
  }, []);

  const toggleSalesRepOnly = useCallback(() => {
    dispatch({ type: 'toggle-sales-rep-only' });
  }, []);

  return (
    <quoteListContext.Provider
      value={{
        state,
        toggleShow,
        setPage,
        setSortBy,
        setSortOrder,
        setSearch,
        setSearchColumn,
        toggleSalesRepOnly,
        saveState,
      }}
    >
      {children}
    </quoteListContext.Provider>
  );
}

export function useQuoteListState() {
  return useContext(quoteListContext);
}
